import { Component, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { QuotaService } from '../../../quotas/services/quota.service';
import { OepSignatureDialogComponent, OepSignatureDialogData } from '../../../../shared/components/oep-signature-dialog/oep-signature-dialog.component';

interface MeatType {
  id: number;
  nameRu: string;
  nameKy: string;
  alias: string;
}

interface QuotaLimit {
  meatType: MeatType;
  totalLimitVolume: number | null; // в тоннах для отображения
}

@Component({
  selector: 'app-add-limit-quota-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    TranslateModule
  ],
  templateUrl: './add-limit-quota-dialog.component.html',
  styleUrls: ['./add-limit-quota-dialog.component.css']
})
export class AddLimitQuotaDialogComponent implements OnInit {
  private quotaService = inject(QuotaService);
  private dialog = inject(MatDialog);
  private dialogRef = inject(MatDialogRef<AddLimitQuotaDialogComponent>);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);

  loading = signal(false);
  loadingMeatTypes = signal(false);
  errorMessage = signal('');
  quotaLimits = signal<QuotaLimit[]>([]);

  personIdnp = '';
  organizationInn = '';

  ngOnInit(): void {
    this.loadMeatTypes();
  }

  private loadMeatTypes(): void {
    this.loadingMeatTypes.set(true);

    this.quotaService.getMeatTypes().subscribe({
      next: (response) => {
        if (response.success && response.res) {
          const meatTypes = Array.isArray(response.res)
            ? response.res
            : (response.res.content || []);

          const limits = meatTypes.map((mt: MeatType) => ({
            meatType: mt,
            totalLimitVolume: null
          }));
          this.quotaLimits.set(limits);
        }
        this.loadingMeatTypes.set(false);
      },
      error: (error) => {
        console.error('Error loading meat types:', error);
        this.errorMessage.set('Ошибка загрузки типов мяса');
        this.loadingMeatTypes.set(false);
      }
    });
  }

  isFormValid(): boolean {
    return !!(this.personIdnp && this.organizationInn && this.quotaLimits().some(l => l.totalLimitVolume && l.totalLimitVolume > 0));
  }

  submit(): void {
    if (!this.isFormValid()) return;

    this.errorMessage.set('');

    // Открываем диалог OEP подписи
    const signatureDialogRef = this.dialog.open(OepSignatureDialogComponent, {
      width: '400px',
      disableClose: true,
      data: {
        organizationInn: this.organizationInn,
        personIdnp: this.personIdnp,
        title: 'Подпись для добавления лимитов'
      } as OepSignatureDialogData
    });

    signatureDialogRef.afterClosed().subscribe(result => {
      if (result?.success && result.token) {
        this.createQuotasBatch(result.token);
      }
    });
  }

  private createQuotasBatch(token: string): void {
    this.loading.set(true);

    const limits = this.quotaLimits().map(l => ({
      meatType: l.meatType,
      totalLimitVolume: (l.totalLimitVolume || 0) * 1000 // tons to kg
    }));

    this.quotaService.createQuotasBatch({
      year: new Date().getFullYear(),
      limits: limits,
      token: token,
      personIdnp: this.personIdnp,
      organizationInn: this.organizationInn
    }).subscribe({
      next: (response) => {
        this.loading.set(false);
        if (response.success) {
          this.snackBar.open('Лимиты квот успешно добавлены', 'OK', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top'
          });
          this.dialogRef.close({ success: true });
        } else {
          this.errorMessage.set(response.msg || 'Ошибка сохранения лимитов');
        }
      },
      error: (error) => {
        console.error('Error creating quota limits:', error);
        this.errorMessage.set('Ошибка сохранения лимитов');
        this.loading.set(false);
      }
    });
  }

  close(): void {
    this.dialogRef.close({ success: false });
  }

  getMeatTypeName(meatType: any): string {
    if (!meatType) return '';
    const lang = this.translate.currentLang || 'ru';
    return lang === 'ky' ? meatType.nameKy : meatType.nameRu;
  }
}
